import { Form, InputNumber, Select, DatePicker, Row, Col, Divider, Typography, Input } from "antd";
import dayjs from "dayjs";
import { useEffect, useState, useMemo } from "react";
import { getStockProducto } from "../../../../services/financiero/finEntregasService";
const { Option } = Select;
const { Text } = Typography;
export default function EntregaForm({ form, initialValues, participantes = [], productos = [], municipios = [], departamentos = [], proyectos = [], onFinish }) {

  const [departamentoSeleccionado, setDepartamentoSeleccionado] = useState(null);
  const [stockDisponible, setStockDisponible] = useState(0);
  const productoSeleccionado = Form.useWatch("oid_producto", form);

  const cantidadOriginal = Number(initialValues?.cantidad || 0);



  /* ================= STOCK MAX EDITABLE ================= */

  const stockMaximoEditable = Number(stockDisponible) + cantidadOriginal;

  /* ================= CONSULTAR STOCK ================= */

  useEffect(() => {

    if (!productoSeleccionado) {
      setStockDisponible(0);
      return;
    }

    const consultarStock = async () => {
      try {
        const data = await getStockProducto(productoSeleccionado);
        setStockDisponible(Number(data.stock_disponible || 0));
      } catch (error) {
        console.error(error);
      }
    };
    consultarStock();
  }, [productoSeleccionado]);

  useEffect(() => {
    if (initialValues && form) {
      const values = {
        oid_participante: initialValues.oid_participante,
        oid_proyecto: initialValues.oid_proyecto,
        oid_depto: initialValues.oid_depto,
        oid_municipio: initialValues.oid_municipio,
        oid_producto: initialValues.oid_producto,
        cantidad: initialValues.cantidad,
        tipo_entrega: initialValues.tipo_entrega,
        carpeta: initialValues.carpeta,
        nombre_archivo: initialValues.nombre_archivo,
        fecha_entrega: initialValues.fecha_entrega
          ? dayjs(initialValues.fecha_entrega)
          : null
      };
      setDepartamentoSeleccionado(initialValues.oid_depto);
      form.setFieldsValue(values);
    }
  }, [initialValues, form]);

  const municipiosFiltrados = useMemo(() => {
    if (!departamentoSeleccionado) return [];
    return municipios.filter(
      (m) => Number(m.oid_depto) === Number(departamentoSeleccionado)
    );
  }, [municipios, departamentoSeleccionado]);

  const handleDepartamentoChange = (value) => {
    setDepartamentoSeleccionado(value);
    form.setFieldsValue({
      oid_municipio: null
    });
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onFinish}
    >
      <Divider orientation="left">
        Información del participante
      </Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="oid_participante"
            label="Participante"
            rules={[{ required: true, message: "Seleccione un participante" }]}
          >
            <Select
              showSearch
              placeholder="Buscar participante"
              optionFilterProp="children"
              allowClear
            >
              {participantes.map((p) => (
                <Option key={p.oid} value={p.oid}>
                  {p.nombre_participante}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="oid_proyecto"
            label="Proyecto"
            rules={[{ required: true, message: "Seleccione un proyecto" }]}
          >
            <Select allowClear>
              {proyectos.map((p) => (
                <Option key={p.oid} value={p.oid}>
                  {p.codigo} - {p.proyecto}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation="left">
        Ubicación
      </Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="oid_depto"
            label="Departamento"
            rules={[{ required: true }]}
          >
            <Select onChange={handleDepartamentoChange}>
              {departamentos.map((d) => (
                <Option key={d.oid} value={d.oid}>
                  {d.departamento}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="oid_municipio"
            label="Municipio"
            rules={[{ required: true }]}
          >
            <Select
              disabled={!departamentoSeleccionado}
              showSearch
              optionFilterProp="children"
            >
              {municipiosFiltrados.map((m) => (
                <Option key={m.oid} value={m.oid}>
                  {m.municipio}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation="left">
        Producto entregado
      </Divider>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="oid_producto"
            label="Producto"
            rules={[{ required: true }]}
          >
            <Select showSearch optionFilterProp="children">
              {productos.map((p) => (
                <Option key={p.oid} value={p.oid}>
                  {p.nombre_producto}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Text type="secondary">
            Stock disponible en inventario: <b>{stockDisponible}</b>
          </Text>
          <br />
          <Text type="secondary">
            Cantidad actual de esta entrega: <b>{cantidadOriginal}</b>
          </Text>
          <br />
          <Text strong>
            Máximo que puede registrar: {stockMaximoEditable}
          </Text>
        </Col>
        <Col span={6}>
          <Form.Item
            name="cantidad"
            label="Cantidad"
            rules={[
              { required: true, message: "Ingrese cantidad" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (value > stockMaximoEditable) {
                    return Promise.reject(
                      new Error(`Stock insuficiente. Máximo permitido: ${stockMaximoEditable}`)
                    );
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber
              min={1}
              max={stockMaximoEditable}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="tipo_entrega"
            label="Tipo entrega"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value={1}>Participante</Option>
              <Option value={2}>Organización</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="fecha_entrega"
            label="Fecha de entrega"
            rules={[{ required: true }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation="left">
        Información de archivo
      </Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="carpeta" label="Carpeta">
            <Select placeholder="Opcional">
              <Option value="documentos">Documentos</Option>
              <Option value="evidencias">Evidencias</Option>
              <Option value="reportes">Reportes</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="nombre_archivo" label="Nombre del archivo">
            <Input placeholder="Nombre del archivo" />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}